/**
 * Alerts (doc 17 §4) — a toast, a minimap ping, and a click that takes you
 * there.
 *
 * The rate limit is the feature. A Reef Tyrant eating a smelter generates one
 * alert every tick it chews; without "one per structure per 10 s" the screen
 * becomes a scrolling apology. Older toasts collapse into "+2 more" rather than
 * pushing the newest one off, because the newest one is the one you need.
 */

import { font } from '../render/font/font.ts';
import { PixelSurface } from '../render/surface.ts';
import { ellipsize } from '../ui/menu.ts';
import { g, UI } from '../ui/theme.ts';
import { drawPanel, type Rect } from '../ui/widgets.ts';

/** Doc 17 §4, verbatim. */
export const PER_STRUCTURE_MS = 10_000;
export const MAX_STACKED = 3;
const LIFE_MS = 3500;
/** Doc 17 §6: toasts slide in from the right over 140 ms and step out over 120. */
const SLIDE_IN_MS = 140;
const SLIDE_OUT_MS = 120;
const ROW_H = 16;

export type Severity = 'normal' | 'loud';

export interface Alert {
  text: string;
  severity: Severity;
  /** Where clicking it takes the camera, in tile space. Null = nowhere. */
  at: { x: number; y: number } | null;
  bornAt: number;
}

export class Alerts {
  private live: Alert[] = [];
  private readonly lastFor = new Map<number, number>();
  private rects: Rect[] = [];
  private reducedMotion = false;

  configure(reducedMotion: boolean): void {
    this.reducedMotion = reducedMotion;
  }

  /**
   * `key` is the structure the alert is about — the rate limit is per key, so
   * one building being eaten cannot drown out a different one being eaten.
   * Pass -1 for anything that is not about a structure.
   */
  raise(key: number, text: string, severity: Severity, at: Alert['at'], now: number): boolean {
    if (key >= 0) {
      const last = this.lastFor.get(key) ?? -Infinity;
      if (now - last < PER_STRUCTURE_MS) return false;
      this.lastFor.set(key, now);
    }
    this.live.push({ text, severity, at, bornAt: now });
    return true;
  }

  step(now: number): void {
    if (this.live.length === 0) return;
    this.live = this.live.filter((a) => now - a.bornAt < LIFE_MS + SLIDE_OUT_MS);
  }

  /** Live pings for the minimap, newest first. */
  pings(): Array<{ x: number; y: number; severity: Severity }> {
    const out: Array<{ x: number; y: number; severity: Severity }> = [];
    for (const a of this.live) if (a.at) out.push({ ...a.at, severity: a.severity });
    return out;
  }

  /** Which alert was clicked, if any — the caller jumps the camera to it. */
  hit(x: number, y: number): Alert | null {
    for (let i = 0; i < this.rects.length; i++) {
      const r = this.rects[i]!;
      if (x >= r.x && y >= r.y && x < r.x + r.w && y < r.y + r.h) return this.shown()[i] ?? null;
    }
    return null;
  }

  clear(): void {
    this.live = [];
    this.rects = [];
    this.lastFor.clear();
  }

  /** Newest three. Anything older is a count, not a line. */
  private shown(): Alert[] {
    return this.live.slice(-MAX_STACKED).reverse();
  }

  /** Top-right (doc 17 §8's one-panel-per-corner rule). Returns nothing. */
  draw(s: PixelSurface, now: number, topRight: { x: number; y: number; w: number }): void {
    this.rects = [];
    const shown = this.shown();
    if (shown.length === 0) return;
    const micro = font('micro');
    let y = topRight.y;
    for (const a of shown) {
      const age = now - a.bornAt;
      // slide in from the right, hold, step out — doc 17 §6
      const slide = this.reducedMotion
        ? 0
        : age < SLIDE_IN_MS
          ? Math.round((1 - age / SLIDE_IN_MS) * 24)
          : age > LIFE_MS
            ? Math.round(((age - LIFE_MS) / SLIDE_OUT_MS) * 24)
            : 0;
      if (age > LIFE_MS + SLIDE_OUT_MS) continue;
      // clipped to the panel, never past it: an alert that runs off the right
      // edge of the screen is the one thing worse than no alert (doc 10)
      const text = ellipsize('micro', a.text, topRight.w - g(4), 1);
      const w = Math.min(topRight.w, micro.measure(text, { caps: true, letterSpacing: 1 }) + g(4));
      const r: Rect = { x: topRight.x + topRight.w - w + slide, y, w, h: ROW_H };
      drawPanel(s, r);
      if (a.severity === 'loud') s.strokeRect(r.x + 1, r.y + 1, r.w - 2, r.h - 2, UI.critical);
      micro.draw(s, text, r.x + g(2), r.y + ((ROW_H - micro.capHeight) >> 1), a.severity === 'loud' ? UI.critical : UI.ink, {
        caps: true,
        letterSpacing: 1,
      });
      this.rects.push(r);
      y += ROW_H + g(1);
    }
    const hidden = this.live.length - shown.length;
    if (hidden > 0) {
      micro.draw(s, `+${hidden} MORE`, topRight.x + topRight.w - g(12), y + 1, UI.inkDim, {
        caps: true,
        letterSpacing: 1,
      });
    }
  }
}
